import * as React from 'react';
import Box from '@mui/material/Box';
import {
  FormControl,
  Button,
  Divider,
  Stack,
  Typography,
  DialogContentText
} from '@mui/material';
import Grid from "@mui/material/Grid2";
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { useStoreActions } from 'easy-peasy';
import api from '../../../services/api';
import { pt } from 'yup-locale-pt';
import {
  Checkbox,
  TextField,
  Autocomplete,
  IntegerField
} from '../../../components';
import { baseInfo, periodicidadeCenso } from './static.js';
import FloatField from '../../../components/FloatField';

yup.setLocale(pt);

const Registros = ({ ano, empresa }) => {
  let startLoading = useStoreActions(actions => actions.nav.startLoading);
  let stopLoading = useStoreActions(actions => actions.nav.stopLoading);

  const schema = yup
    .object({
      censoDiversidade: yup
        .boolean()
        .label(
          'A minha empresa realiza censo de diversidade'
        ),
      periodicidadeCenso: yup
        .string()
        .nullable()
        .label(
          'Periodicidade'
        ),
        censoDiversidadeNoAno: yup
        .boolean()
        .label(
          'A minha empresa realizou neste ano de referência pesquisa de censo de diversidade.'
        ),
      baseInformacao: yup.string().nullable().label('Base informações'),
      infosVindasAdmissao: yup
        .boolean()
        .label(
          'O registro admissional continha informações de raça, orientação sexual e identidade de gênero separadamente.'
        ),
      totalLider: yup
        .number()
        .moreThan(-1)
        .integer()
        .nullable()
        .nanable()
        .label('Liderança'),
      totalAltaLider: yup
        .number()
        .moreThan(-1)
        .integer()
        .nullable()
        .nanable()
        .label('Alta liderança'),
      mulheres: yup
        .number()
        .moreThan(-1)
        .integer()
        .nullable()
        .nanable()
        .label('Mulheres'),
      mulheresLider: yup
        .number()
        .moreThan(-1)
        .integer()
        .nullable()
        .nanable()
        .label('Mulheres liderança'),
      mulheresAltaLider: yup
        .number()
        .moreThan(-1)
        .positive()
        .nullable()
        .nanable()
        .label('Mulheres alta liderança'),
      pcds: yup
        .number()
        .moreThan(-1)
        .integer()
        .nullable()
        .nanable()
        .label('PCDs'),
      pcdsLider: yup
        .number()
        .moreThan(-1)
        .integer()
        .nullable()
        .nanable()
        .label('PCDs liderança'),
      pcdsAltaLider: yup
        .number()
        .moreThan(-1)
        .integer()
        .nullable()
        .nanable()
        .label('PCDs alta liderança'),
      pretosPardos: yup
        .number()
        .moreThan(-1)
        .integer()
        .nullable()
        .nanable()
        .label('Pessoas negras'),
      pretosPardosLider: yup
        .number()
        .moreThan(-1)
        .integer()
        .nullable()
        .nanable()
        .label('Pessoas negras em posição de liderança'),
      pretosPardosAltaLider: yup
        .number()
        .moreThan(-1)
        .integer()
        .nullable()
        .nanable()
        .label('Pessoas negras em posição alta liderança'),
      lgbtqia: yup
        .number()
        .moreThan(-1)
        .integer()
        .nullable()
        .nanable()
        .label('LGBTQIA+'),
      lgbtqiaLider: yup
        .number()
        .moreThan(-1)
        .integer()
        .nullable()
        .nanable()
        .label('LGBTQIA+ liderança'),
      lgbtqiaAltaLider: yup
        .number()
        .moreThan(-1)
        .integer()
        .nullable()
        .nanable()
        .label('LGBTQIA+ alta liderança'),
      iniciativaDiversi: yup
        .boolean()
        .label(
          'A minha empresa realizou neste ano iniciativas para promover a diversidade para pessoas LGBTQIA+, pessoas pretas ou pardas, pessoas com deficiência ou mulheres.'
        ),
      programaDiversi: yup
        .boolean()
        .label(
          'A organização possui: Programa de Diversidade, Políticas e/ou Práticas voltadas a D&I'
        ),
      beneficiosDiversi: yup
        .boolean()
        .label(
          'Benefícios: Possuem benefícios customizados pelas necessidades dos grupos minorizados'
        ),
      investimentoTreinamento: yup
        .number()
        .nullable()
        .positive()
        .float()
        .nanable()
        .label(
          'Investimento (em R$)'
        ),
      publico: yup
        .string()
        .max(1000, 'Máximo 1000 caracteres')
        .label(
          'Público'
        )
        .nullable(),
      atividade: yup
        .string()
        .max(1000, 'Máximo 1000 caracteres')
        .label(
          'O que é realizado'
        )
        .nullable(),
      acessBanheiro: yup
        .boolean()
        .label(
          'Banheiros'
        ),
      acessVestiario: yup
        .boolean()
        .label(
          'Vestiários'
        ),
      acessSalaDescanso: yup
        .boolean()
        .label(
          'Sala de descanso'
        ),
      acessSalaExtracaoLeite: yup
        .boolean()
        .label(
          'Sala para extração de leite'
        ),
      investimentoAcoes: yup
        .number()
        .nullable()
        .positive()
        .float()
        .nanable()
        .label(
          'Investimento (em R$)'
        ),
      obsDi: yup
        .string()
        .max(1000, 'Máximo 1000 caracteres')
        .label(
          'Por favor, compartilhe conosco uma boa prática de D&I que a sua empresa realizou neste ano'
        )
        .nullable(),
      obsGeral: yup
        .string()
        .max(1000, 'Máximo 1000 caracteres')
        .label(
          'Deixe seu comentário ou outras informações que considerar importante'
        )
        .nullable()
    })
    .required();

  const useFormRef = useForm({
    mode: 'onTouched',
    resolver: yupResolver(schema)
  });

  const {
    handleSubmit,
    reset,
    watch,
    formState: { isDirty, isSubmitting }
  } = useFormRef;

  const loadRegistros = async mount => {
    startLoading();
    try {
      let url = '05/registros/empresa/' + empresa + '/' + ano;
      let getRegistros = await api.http.get(url);
      if (mount.isMounted) {
        reset(getRegistros.data);
      }
      stopLoading();
    } catch (error) {
      if (error.response && error.response.status == 404) {
        if (mount.isMounted) {
          reset({
            ano: ano,
            censoDiversidade: false,
            periodicidadeCenso: null,
            censoDiversidadeNoAno: false,
            baseInformacao: null,
            infosVindasAdmissao: false,
            totalLider: '',
            totalAltaLider: '',
            mulheres: '',
            mulheresLider: '',
            mulheresAltaLider: '',
            pcds: '',
            pcdsLider: '',
            pcdsAltaLider: '',
            pretosPardos: '',
            pretosPardosLider: '',
            pretosPardosAltaLider: '',
            lgbtqia: '',
            lgbtqiaLider: '',
            lgbtqiaAltaLider: '',
            iniciativaDiversi: false,
            programaDiversi: false,
            beneficiosDiversi: false,
            investimentoTreinamento: '',
            publico: '',
            atividade: '',
            acessBanheiro: false,
            acessVestiario: false,
            acessSalaDescanso: false,
            acessSalaExtracaoLeite: false,
            investimentoAcoes: '',
            obsDi: '',
            obsGeral: ''
          });
        }
        stopLoading();
      } else {
        stopLoading({
          message: error.toString(),
          severity: 'error'
        });
      }
    }
  };

  const watchBaseInfo = watch('baseInformacao');
  const disabled = () => {
    return watchBaseInfo != 'registAdmiss';
  };

  const watchCenso = watch('censoDiversidade');
  const disabledCenso = () => {
    return !watchCenso;
  };

  const submit = async data => {
    startLoading();
    try {
      let result;
      if (data.id) {
        result = await api.http.put(
          '05/registros/empresa/' + empresa + '/' + data.id,
          { ...data }
        );
      } else {
        result = await api.http.post('05/registros/empresa/' + empresa, {
          ...data,
          ano: ano
        });
      }
      reset(result.data);
      stopLoading({ severity: 'success' });
    } catch (error) {
      stopLoading({
        message: error.toString(),
        severity: 'error'
      });
      throw error;
    }
  };

  React.useEffect(() => {
    let mount = {
      isMounted: true
    };
    loadRegistros(mount);
    return () => {
      mount.isMounted = false;
    };
  }, [ano, empresa]);

  return (<>
    <Divider>
      <h3>Censo</h3>
    </Divider>
    <FormControl
      sx={{
        '& .MuiFormControlLabel-root': { display: 'inline-block' }
      }}
    >
      <Checkbox
        field="censoDiversidade"
        schema={schema}
        useFormRef={useFormRef}
      />

      <Grid
        container
        sx={{
          '& .MuiTypography-root': { textAlign: 'right' },
          '& .MuiGrid-root': { p: 1 }
        }}
      >
        <Grid
          size={{
            md: 6
          }}>
          <Typography sx={{ p: 2 }}>
            Periodicidade do censo de diversidade:
          </Typography>
        </Grid>
        <Grid
          size={{
            md: 6
          }}>
          <Autocomplete
            sx={{ width: 280 }}
            field="periodicidadeCenso"
            options={periodicidadeCenso}
            schema={schema}
            useFormRef={useFormRef}
            required
            disabled={disabledCenso()}
          />
        </Grid>
      </Grid>
      
      <Checkbox
        field="censoDiversidadeNoAno"
        schema={schema}
        useFormRef={useFormRef}
        disabled={disabledCenso()}
      />
    </FormControl>
    <Grid
      container
      sx={{
        '& .MuiTypography-root': { textAlign: 'right' },
        '& .MuiGrid-root': { p: 1 }
      }}
    >
      <Grid
        size={{
          md: 6
        }}>
        <Typography sx={{ p: 2 }}>
          Essas informações são baseadas em:
        </Typography>
      </Grid>
      <Grid
        size={{
          md: 6
        }}>
        <Autocomplete
          sx={{ width: 280 }}
          field="baseInformacao"
          options={baseInfo}
          schema={schema}
          useFormRef={useFormRef}
          required
        />
      </Grid>
    </Grid>
    <FormControl
      sx={{ '& .MuiFormControlLabel-root': { display: 'inline-block' } }}
    >
      <Checkbox
        field="infosVindasAdmissao"
        schema={schema}
        useFormRef={useFormRef}
        disabled={disabled()}
      />
    </FormControl>
    <Divider sx={{ mt: 3 }}>
      <h3>Dados</h3>
    </Divider>
    <DialogContentText sx={{ mb: 2 }}>
      Liderança: Todo(a) empregado(a) que possui gestão de equipe (inclui a
      alta liderança).
    </DialogContentText>
    <DialogContentText sx={{ mb: 4 }}>
      Alta Liderança: Cargos de liderança que estejam até 3 níveis abaixo do
      primeiro líder da organização no Brasil, incluindo o Número 1".
    </DialogContentText>
    <Grid
      container
      spacing={2}
      sx={{
        '& .MuiTypography-root': { textAlign: 'right', p: 1 },
        '& .MuiGrid-root': { p: 1 }
      }}
    >
      <Grid
        size={{
          md: 10
        }}>
        <Typography>
          Número total de empregados em posição de liderança:
        </Typography>
      </Grid>
      <Grid
        size={{
          md: 2
        }}>
        <FormControl fullWidth>
          <IntegerField
            field="totalLider"
            schema={schema}
            useFormRef={useFormRef}
            size="small"
          />
        </FormControl>
      </Grid>

      <Grid
        size={{
          md: 10
        }}>
        <Typography>
          Número total de empregados em posição de alta liderança:
        </Typography>
      </Grid>
      <Grid
        size={{
          md: 2
        }}>
        <FormControl fullWidth>
          <IntegerField
            field="totalAltaLider"
            schema={schema}
            useFormRef={useFormRef}
            size="small"
          />
        </FormControl>
      </Grid>

      <Grid
        size={{
          md: 10
        }}>
        <Typography>Número de mulheres:</Typography>
      </Grid>
      <Grid
        size={{
          md: 2
        }}>
        <FormControl fullWidth>
          <IntegerField
            field="mulheres"
            schema={schema}
            useFormRef={useFormRef}
            size="small"
          />
        </FormControl>
      </Grid>

      <Grid
        size={{
          md: 10
        }}>
        <Typography>Número de mulheres em posição de liderança:</Typography>
      </Grid>
      <Grid
        size={{
          md: 2
        }}>
        <FormControl fullWidth>
          <IntegerField
            field="mulheresLider"
            schema={schema}
            useFormRef={useFormRef}
            size="small"
          />
        </FormControl>
      </Grid>

      <Grid
        size={{
          md: 10
        }}>
        <Typography>
          Número de mulheres em posição de alta liderança:
        </Typography>
      </Grid>
      <Grid
        size={{
          md: 2
        }}>
        <FormControl fullWidth>
          <IntegerField
            field="mulheresAltaLider"
            schema={schema}
            useFormRef={useFormRef}
            size="small"
          />
        </FormControl>
      </Grid>

      <Grid
        size={{
          md: 10
        }}>
        <Typography>Número de PCDs (Pessoas com Deficiência):</Typography>
      </Grid>
      <Grid
        size={{
          md: 2
        }}>
        <FormControl fullWidth>
          <IntegerField
            field="pcds"
            schema={schema}
            useFormRef={useFormRef}
            size="small"
          />
        </FormControl>
      </Grid>

      <Grid
        size={{
          md: 10
        }}>
        <Typography>Número de PCDs em posição de liderança:</Typography>
      </Grid>
      <Grid
        size={{
          md: 2
        }}>
        <FormControl fullWidth>
          <IntegerField
            field="pcdsLider"
            schema={schema}
            useFormRef={useFormRef}
            size="small"
          />
        </FormControl>
      </Grid>

      <Grid
        size={{
          md: 10
        }}>
        <Typography>Número de PCDs em posição de alta liderança:</Typography>
      </Grid>
      <Grid
        size={{
          md: 2
        }}>
        <FormControl fullWidth>
          <IntegerField
            field="pcdsAltaLider"
            schema={schema}
            useFormRef={useFormRef}
            size="small"
          />
        </FormControl>
      </Grid>

      <Grid
        size={{
          md: 10
        }}>
        <Typography>
          Número de pessoas que se autodeclaram negras (pretas ou pardas):
        </Typography>
      </Grid>
      <Grid
        size={{
          md: 2
        }}>
        <FormControl fullWidth>
          <IntegerField
            field="pretosPardos"
            schema={schema}
            useFormRef={useFormRef}
            size="small"
          />
        </FormControl>
      </Grid>

      <Grid
        size={{
          md: 10
        }}>
        <Typography>
          Número de pessoas que se autodeclaram negras (pretas ou pardas) em
          posição de liderança:
        </Typography>
      </Grid>
      <Grid
        size={{
          md: 2
        }}>
        <FormControl fullWidth>
          <IntegerField
            field="pretosPardosLider"
            schema={schema}
            useFormRef={useFormRef}
            size="small"
          />
        </FormControl>
      </Grid>

      <Grid
        size={{
          md: 10
        }}>
        <Typography>
          Número de pessoas que se autodeclaram negras (pretas ou pardas) em
          posição de alta liderança:
        </Typography>
      </Grid>
      <Grid
        size={{
          md: 2
        }}>
        <FormControl fullWidth>
          <IntegerField
            field="pretosPardosAltaLider"
            schema={schema}
            useFormRef={useFormRef}
            size="small"
          />
        </FormControl>
      </Grid>

      <Grid
        size={{
          md: 10
        }}>
        <Typography>Número de LGBTQIA+:</Typography>
      </Grid>
      <Grid
        size={{
          md: 2
        }}>
        <FormControl fullWidth>
          <IntegerField
            field="lgbtqia"
            schema={schema}
            useFormRef={useFormRef}
            size="small"
          />
        </FormControl>
      </Grid>

      <Grid
        size={{
          md: 10
        }}>
        <Typography>Número de LGBTQIA+ em posição de liderança:</Typography>
      </Grid>
      <Grid
        size={{
          md: 2
        }}>
        <FormControl fullWidth>
          <IntegerField
            field="lgbtqiaLider"
            schema={schema}
            useFormRef={useFormRef}
            size="small"
          />
        </FormControl>
      </Grid>

      <Grid
        size={{
          md: 10
        }}>
        <Typography>
          Número de LGBTQIA+ em posição de alta liderança:
        </Typography>
      </Grid>
      <Grid
        size={{
          md: 2
        }}>
        <FormControl fullWidth>
          <IntegerField
            field="lgbtqiaAltaLider"
            schema={schema}
            useFormRef={useFormRef}
            size="small"
          />
        </FormControl>
      </Grid>
    </Grid>
    <FormControl
      sx={{ '& .MuiFormControlLabel-root': { display: 'inline-block' } }}
    >
      <Checkbox
        field="iniciativaDiversi"
        schema={schema}
        useFormRef={useFormRef}
      />
      <Checkbox
        field="programaDiversi"
        schema={schema}
        useFormRef={useFormRef}
      />
      <Checkbox
        field="beneficiosDiversi"
        schema={schema}
        useFormRef={useFormRef}
      />
    </FormControl>
    <Typography sx={{ pt: 5 }}>
      Acessibilidade nas unidades (administrativas e industriais) para mulheres e pessoas com deficiência:
    </Typography>
    <Stack direction="row" spacing={4} sx={{ display: 'inline-block' , m:2}}>
      <Checkbox
        field="acessBanheiro"
        schema={schema}
        useFormRef={useFormRef}
      />
      <Checkbox
        field="acessVestiario"
        schema={schema}
        useFormRef={useFormRef}
      />
      <Checkbox
        field="acessSalaDescanso"
        schema={schema}
        useFormRef={useFormRef}
      />
      <Checkbox
        field="acessSalaExtracaoLeite"
        schema={schema}
        useFormRef={useFormRef}
      />
    </Stack>
    <Divider sx={{ mt: 3 }}>
      <h3>Treinamento em D&I</h3>
    </Divider>
    <FormControl fullWidth sx={{ mb: 2 }}>
      <Grid
          container
          sx={{
            '& .MuiTypography-root': { textAlign: 'right' },
            '& .MuiGrid-root': { p: 1 }
          }}
        >
        <Grid
          size={{
            md: 6
          }}>
            <Typography sx={{ p: 2 }}>
              Investimento em treinamento:
            </Typography>
        </Grid>
        <Grid >
          <FloatField
            field="investimentoTreinamento"
            schema={schema}
            useFormRef={useFormRef}
          />
        </Grid> 
      </Grid>
    </FormControl>
    <FormControl fullWidth sx={{ mb: 2 }}>
      <TextField
        field="publico"
        multiline
        rows={4}
        schema={schema}
        useFormRef={useFormRef}
        inputProps={{ maxLength: 1000 }}
      />
    </FormControl>
    <FormControl fullWidth sx={{ mb: 2 }}>
      <TextField
        field="atividade"
        multiline
        rows={4}
        schema={schema}
        useFormRef={useFormRef}
        inputProps={{ maxLength: 1000 }}
      />
    </FormControl>
    <Stack direction="row" sx={{ textAlign: 'left', alignItems: 'center' }}  >
        <Typography sx={{ p: 2 }}>
          Valor investido na comunidade em ações voltadas a pessoas pretas, pardas, LGBTI+, gênero, pessoa com deficiência:
        </Typography>
      <FormControl sx={{ width: 300 }}>
        <FloatField
          field="investimentoAcoes"
          schema={schema}
          useFormRef={useFormRef}
        />
      </FormControl>
    </Stack>
    <Divider sx={{ mt: 3 }}>
      <h3>Observações Finais</h3>
    </Divider>
    <FormControl fullWidth sx={{ mb: 2 }}>
      <TextField
        field="obsDi"
        multiline
        rows={4}
        schema={schema}
        useFormRef={useFormRef}
        inputProps={{ maxLength: 1000 }}
      />
    </FormControl>
    <FormControl fullWidth sx={{ mb: 2 }}>
      <TextField
        field="obsGeral"
        multiline
        rows={4}
        schema={schema}
        useFormRef={useFormRef}
        inputProps={{ maxLength: 1000 }}
      />
    </FormControl>
    <Box>
      <Button
        variant="outlined"
        sx={{ mr: 2 }}
        onClick={() => {
          reset();
        }}
        disabled={!isDirty || isSubmitting}
      >
        CANCELAR
      </Button>
      <Button
        type="submit"
        variant="contained"
        sx={{ m: 1 }}
        onClick={handleSubmit(submit)}
        disabled={!isDirty || isSubmitting}
      >
        Salvar
      </Button>
    </Box>
  </>);
};

export default Registros;
